import { useI18nContext } from "@i18n/i18n-react";
import { T } from "@i18n/T";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";
import { cn } from "@shadcn/lib/utils";
import { KeyboardAwareDrawer, KeyboardAwareDrawerContent } from "@components/KeyboardAwareDrawer";
import { Button } from "@shadcn/ui/button";
import { DrawerTitle } from "@shadcn/ui/drawer";
import { Navigation, QrCode, X } from "lucide-react";
import { useState } from "react";
import { HubScannerDrawer } from "./HubScannerDrawer";

type HubConnectionDrawerProps = {
	isOpen: boolean;
	onOpenChange: (open: boolean) => void;
	hubData: {
		id: string;
		name: string;
		lat?: number;
		lng?: number;
	};
	onHubConnected?: (hub: { id: string; name: string; side?: "A" | "B" }) => void;
	showLoginMessage?: boolean;
	expectedSide?: string;
	description?: string;
};

export const HubConnectionDrawer = (props: HubConnectionDrawerProps) => {
	const { isOpen, onOpenChange, hubData, onHubConnected, expectedSide, description } = props;
	const [isScannerOpen, setIsScannerOpen] = useState(false);
	const context = useI18nContext();
	const LL = context.LL;
	const i18n = LL["new-ui"];

	return (
		<>
			<KeyboardAwareDrawer open={isOpen && !isScannerOpen} onOpenChange={onOpenChange}>
				<KeyboardAwareDrawerContent
					hasHandle={false}
					className={cn(
						"overflow-hidden border-t-0 bg-card text-card-foreground shadow-xl",
						"lg:inset-x-auto lg:left-1/2 lg:right-auto lg:w-[calc(100vw-2rem)] lg:max-w-[1038px] lg:-translate-x-1/2 lg:rounded-t-xl lg:border lg:border-b-0"
					)}
				>
					<VisuallyHidden>
						<DrawerTitle>
							<T>{i18n.CONNECT_TO_HUB_TITLE()}</T>
						</DrawerTitle>
					</VisuallyHidden>
					<div className="mx-auto w-full lg:max-w-[528px]">
						<div className="px-6 py-6">
							<div className="flex items-start justify-between gap-3">
								<div className="flex min-w-0 flex-1 flex-col gap-0.5">
									<h2 className="font-breit text-2xl font-normal leading-8 tracking-tighter text-card-foreground">
										<T>{i18n.CONNECT_TO_PROCEED()}</T>
									</h2>
									<p className="text-sm leading-5 text-muted-foreground">
										<T>{description ?? i18n.CONNECT_INSTRUCTIONS()}</T>
									</p>
								</div>
								<Button variant="ghost" size="icon" aria-label="Close" onClick={() => onOpenChange(false)}>
									<X className="h-4 w-4" />
								</Button>
							</div>
						</div>
						<div className="flex flex-col gap-2.5 border-t px-6 py-4 font-breit text-sm font-normal">
							{hubData.lat && hubData.lng ? (
								<Button variant="outline" size="lg" className="w-full" asChild>
									<a
										href={`https://www.google.com/maps/dir/?api=1&destination=${hubData.lat},${hubData.lng}`}
										target="_blank"
										rel="noopener noreferrer"
									>
										<Navigation className="mr-2 h-4 w-4" />
										<T>{i18n.NAVIGATE_TO_HUB({ hubName: hubData.name })}</T>
									</a>
								</Button>
							) : null}
							<Button
								size="lg"
								className="w-full"
								onClick={() => {
									// Don't close the drawer immediately, just open the scanner
									setIsScannerOpen(true);
								}}
							>
								<QrCode className="mr-2 h-4 w-4" />
								<T>{i18n.CONNECT_TO_HUB({ hubName: hubData.name })}</T>
							</Button>
						</div>
					</div>
				</KeyboardAwareDrawerContent>
			</KeyboardAwareDrawer>

			<HubScannerDrawer
				isOpen={isScannerOpen}
				onClose={() => {
					setIsScannerOpen(false);
					// When scanner closes without connecting, just close everything
					onOpenChange(false);
				}}
				expectedHubId={hubData.id}
				expectedSide={expectedSide}
				onHubConnected={(hub) => {
					// When successfully connected, close everything and call the callback
					setIsScannerOpen(false);
					onOpenChange(false);
					if (onHubConnected) {
						onHubConnected(hub);
					}
				}}
			/>
		</>
	);
};
